/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Aui 를 사용하기 위한 뷰포트 클래스를 정의한다.
 *
 * @file /src/base/Viewport.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 14.
 */
import * as parent from '@base/Base';
import { Panel } from '@components/panel/Panel';
import { Taskbar } from '@components/taskbar/Taskbar';
import { Task } from '@components/taskbar/elements/Task';
import { Navigation } from '@components/navigation/Navigation';
import { Panel as Desktop } from '@components/desktop/Panel';
import { Window } from '@components/window/Window';
import { Renderer } from '@utils/Renderer';
import { Language, LanguageText } from '@utils/Language';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Resizer } from '@utils/Resizer';
import { Message } from '@components/message/Message';
import { Exceptions } from '@utils/Exceptions';

export interface PanelProvider {
    getTaskMode?(): 'single' | 'multi';
    getIcon(): string;
    getTitle(): LanguageText;
    getUrl(is_base: boolean): string;
    setUrl?(url: string, task?: Task): void;
    setTask?(task: Task): void;
    getPanel(): Promise<Panel>;
}

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 뷰포트가 준비되었을 때
     */
    ready?: (viewport: T) => Promise<void>;

    /**
     * 뷰포트에 패널이 열렸을 때
     */
    add?: (panel: Panel, viewport: T) => Promise<void>;

    /**
     * 뷰포트에 패널이 활성화되었을 때
     */
    active?: (panel: Panel, viewport: T) => Promise<void>;

    /**
     * 뷰포트의 패널이 닫혔을 때
     */
    close?: (panel: Panel, viewport: T) => Promise<void>;

    /**
     * 뷰포트에 패널이 비활성화되었을 때
     */
    deactive?: (panel: Panel, viewport: T) => Promise<void>;

    /**
     * 뷰포트에 활성화할 패널을 가져오기 위한 패널로더
     */
    panelLoader?: (url: string) => Promise<boolean | PanelProvider>;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 뷰포트 고유값
     */
    id: string;

    /**
     * Aui 기본경로
     */
    path?: string;

    /**
     * 중앙 컴포넌트
     */
    panel?: Panel;

    /**
     * 내비게이션
     */
    navigation?: Navigation;

    /**
     * 작업표시줄
     */
    taskbar?: Taskbar;

    /**
     * 데스크탑
     */
    desktop?: Desktop;

    /**
     * 뷰포트에 활성화할 패널을 가져오기 위한 패널로더
     */
    panelLoader?: (url: string) => Promise<boolean | PanelProvider>;

    /**
     * 뷰포트를 랜더링하기 위한 HTMLElement
     */
    renderTo: HTMLElement;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;

    /**
     * 디버그모드 여부
     */
    debug?: boolean;
}

export class Viewport extends parent.Base {
    /**
     * 뷰포트 고유값
     */
    id: string;

    /**
     * Aui 기본 타이틀
     */
    title: string;

    /**
     * Aui 기본경로
     */
    path: string = null;

    /**
     * 패널
     */
    panel: Panel | null;

    /**
     * 내비게이션
     */
    navigation: Navigation | null;

    /**
     * 작업표시줄
     */
    taskbar: Taskbar | null;

    /**
     * 데스크탑
     */
    desktop: Desktop | null;

    /**
     * 뷰포트에 활성화할 패널을 가져오기 위한 패널로더
     */
    panelLoader: (url: string) => Promise<boolean | PanelProvider> = null;

    /**
     * 직전 작업
     */
    prevTask: Task = null;

    /**
     * 현재작업
     */
    task: Task = null;

    /**
     * 최상위 Dom
     */
    $viewport: Dom = null;

    /**
     * 패널 Dom
     */
    $panel: Dom = null;

    /**
     * Aui 를 사용하기 위한 뷰포트 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Viewport>) {
        super(properties);

        this.id = this.properties.id ?? 'Aui-Viewport-' + new Date().getTime();
        this.title = document.title;
        this.path = this.properties.path ?? null;
        this.panel = null;
        this.navigation = this.properties.navigation ?? null;
        this.taskbar = this.properties.taskbar;
        this.desktop = this.properties.desktop ?? null;
        this.panelLoader = this.properties.panelLoader ?? null;

        this.panel?.setViewport(this);
        this.navigation?.setViewport(this);
        this.taskbar?.setViewport(this);
        this.desktop?.setViewport(this);

        Language.setBase(this.getDir() + '/assets');
        Window.setViewport(this);
        Resizer.setViewport(this);
        Exceptions.init(this.properties.debug === true);
        Exceptions.setErrorHandler((e, handler) => {
            Message.error({ message: e.getMessage(), handler: handler });
            if (e.cause !== null) {
                Exceptions.error(e.cause);
            }
        });
    }

    /**
     * Aui 기본경로를 가져온다.
     *
     * @returns 기본경로
     */
    getDir(): string {
        if (this.path === null) {
            const scripts = document.querySelectorAll('script');
            for (const script of scripts) {
                const src = script.getAttribute('src');
                if (src && src.includes('/Aui.js')) {
                    this.path = src.replace(/\/Aui\.js(.*?)$/, '');
                    break;
                }

                if (src && src.includes('/src/utils/Utils.mjs')) {
                    this.path = src.replace(/\/src\/utils\/Utils\.mjs(.*?)$/, '');
                    break;
                }
            }

            if (this.path == null) {
                this.path = '';
            }

            try {
                const url = new URL(this.path, window.location.href);
                this.path = url.pathname;
            } catch (e) {
                this.path = '';
                return null;
            }
        }

        return this.path;
    }

    /**
     * 최상위 Dom 을 가져온다.
     *
     * @returns 최상위 Dom
     */
    $getViewport(): Dom | null {
        if (this.$viewport === null) {
            this.$viewport = Html.create('div', { 'data-viewport': this.id });
        }

        return this.$viewport;
    }

    /**
     * 최상위 Dom 을 가져온다.
     *
     * @returns 최상위 Dom
     */
    $getPanel(): Dom | null {
        if (this.$panel === null) {
            this.$panel = Html.create('div', { 'data-region': 'center' });
        }

        return this.$panel;
    }

    /**
     * 현재 활성화중인 패널을 가져온다.
     *
     * @returns 현재 활성화중인 패널
     */
    getActivePanel(): Panel | null {
        return this.panel;
    }

    /**
     * 패널로더가 존재하는지 확인한다.
     *
     * @returns 패널로더 존재여부
     */
    hasPanelLoader(): boolean {
        return this.panelLoader !== null;
    }

    /**
     * URL 을 이용하여 패널을 오픈한다.
     *
     * @param url - 오픈할 패널 URL
     */
    async open(url: string, target: 'self' | 'blank' = 'self'): Promise<void> {
        const currentUrl = this.getUrl();
        if (target == 'self' && this.hasPanelLoader() == true) {
            const provider = await this.panelLoader(url);
            if (typeof provider == 'object' && typeof provider.getPanel == 'function') {
                if ((await this.reactive(provider)) == false) {
                    const actived = await this.active(provider);
                    if (actived === false) {
                        this.setUrl(currentUrl);
                        Exceptions.warn('[Viewport]', 'FAILED_ACTIVATE_PANEL');
                    }
                }
                return;
            } else if (provider === false) {
                Exceptions.warn('[Viewport]', 'NOT_FOUND_PANEL_PROVIDER');
                return;
            }
        }

        if (target == 'self') {
            location.replace(url);
        } else {
            window.open(url);
        }
    }

    /**
     * 메인패널을 활성화한다.
     *
     * @param target - 활성화할 대상
     * @param url - 변경할 URL (NULL 인 경우 URL 을 변경하지 않는다.)
     */
    loading: boolean = false;
    async active(target: PanelProvider | Panel | Task, url: string = null): Promise<boolean> {
        if (target === null) {
            Exceptions.warn('[Viewport]', 'NULL 은 활성화할 수 없음');
            await this.activeDesktop(true);
            return false;
        }

        if (this.loading === true) {
            Exceptions.warn('[Viewport]', '이전에 활성화요청된 패널을 활성화하고 있어 새로운 패널을 활성화할 수 없음');
            return false;
        }

        this.loading = true;

        let task: Task = null;
        if (target instanceof Task) {
            task = target;
        } else {
            task = new Task(target);

            if (this.taskbar !== null) {
                await this.taskbar.addTask(task);
            }
        }

        await this.deactive();

        this.task = task;
        task.setLoading(true);

        const panel = await task.getPanel();
        if (panel === null) {
            Exceptions.warn('[Viewport]', 'NOT_FOUND_PANEL');
            return false;
        }

        if (panel.viewport?.id !== this.id) {
            panel.setViewport(this);
            panel.addEvent('remove', async (closed) => {
                await this.fireEvent('close', [closed, this]);
            });
        }

        const $panel = this.$getPanel();
        if ($panel.isConnected() == false) {
            this.$getViewport().append($panel);
        }

        if (panel.isRendered() == true) {
            const connecting = new Promise<void>((resolve) => {
                const onConnect = () => {
                    panel.removeEvent('connect', onConnect);
                    resolve();
                };
                panel.addEvent('connect', onConnect);
                $panel.append(panel.$getComponent());
            });
            await connecting;
        } else {
            $panel.append(panel.$getComponent());
        }
        this.panel = panel;

        if (url === null) {
            this.setUrl(await task.getUrl(), await task.getText());
        } else {
            if (target instanceof Task) {
                this.setUrl(await task.getUrl(), await task.getText());
            } else {
                await task.setUrl(url);
            }
        }

        if (this.panel.isRendered() == false) {
            await this.fireEvent('open', [panel, this]);

            try {
                await Renderer.request(() => panel.render());
            } catch (e) {
                Exceptions.print(Exceptions.code('FAILED_TO_LOAD_PANEL', null, null, null, e), async () => {
                    await this.panel.remove();
                });
            }
        }

        task.setLoading(false);
        task.setActive(true);

        /**
         * 작업을 활성화하는 도중, 작업내에서 URL 이 변경되었을 경우
         */
        if (this.getUrl() !== (await task.getUrl())) {
            this.setUrl(await task.getUrl());
        }

        await this.fireEvent('active', [panel, this]);

        this.loading = false;

        return true;
    }

    /**
     * 기존에 활성화중인 동일작업이 있다면, 해당 작업을 재활성화한다.
     *
     * @param provider - 재활성화할 패널 프로바이더
     * @param url - 재활성화할 URL
     * @returns 재활성화여부
     */
    async reactive(provider: PanelProvider, url: string = null): Promise<boolean> {
        if (this.taskbar === null) {
            return false;
        }

        let taskmode: string = 'single';
        if (typeof provider.getTaskMode == 'function') {
            taskmode = provider.getTaskMode();
        }

        if (taskmode == 'multi') {
            return false;
        }

        const tasks = (await this.taskbar.getTasksByUrl(provider.getUrl(true))).toReversed();
        for (const task of tasks) {
            if (url !== null) {
                await task.setUrl(url);
            }

            await task.active();

            /**
             * 작업을 활성화하는 도중, 작업내에서 URL 이 변경되었을 경우
             */
            if (this.getUrl() !== (await task.getUrl())) {
                this.setUrl(await task.getUrl());
            }
            return true;
        }

        return false;
    }

    /**
     * 현재 활성화중인 패널을 비활성화한다.
     */
    async deactive(): Promise<void> {
        if (this.panel !== null) {
            if (this.taskbar === null) {
                await this.panel.remove();
                await this.fireEvent('close', [this.panel, this]);
            } else {
                this.panel.$getComponent().remove();
                await this.fireEvent('deactive', [this.panel, this]);
            }

            this.prevTask = this.task;
            this.task = null;
            this.panel = null;
        }
    }

    /**
     * 직전 작업이 있다면 해당 작업을 활성화한다.
     *
     * @param is_closed - 현재 활성화된 창이 제거되면서 이전작업을 복원하는지 여부
     */
    async activeLatest(is_closed: boolean = false): Promise<void> {
        if (this.prevTask !== null) {
            await this.active(this.prevTask);
        } else {
            Exceptions.warn('[Viewport]', 'activeLatest', 'NOT_FOUND_LATEST_TASK');
            await this.activeDesktop();
        }

        if (is_closed == true) {
            this.prevTask = null;
        }
    }

    /**
     * 데스크탑을 활성화한다.
     *
     * @param is_url - URL 을 변경할지 여부 (viewport 가 최초로 렌더링될때는 URL 을 변경하지 않는다.)
     */
    async activeDesktop(is_url: boolean = true): Promise<void> {
        await this.deactive();

        if (this.desktop === null) {
            this.task = null;
            this.prevTask = null;
            this.panel = null;

            if (is_url == true) {
                this.setUrl(this.navigation.basePath);
            }
        } else if (this.desktop.$getContent().isConnected() === false) {
            const $panel = this.$getPanel();
            $panel.append(this.desktop.$getComponent());
            this.$getViewport().append($panel);

            if (this.desktop.isRendered() == false) {
                await Renderer.request(() => this.desktop.render());
            }

            this.task = null;
            this.prevTask = null;
            this.panel = this.desktop;

            if (is_url == true) {
                history.replaceState(null, '', this.navigation.basePath);
            }
        }
    }

    /**
     * 뷰포트의 현재 URL 을 가져온다.
     *
     * @returns 현재 URL
     */
    getUrl(): string {
        return location.pathname + location.search + location.hash;
    }

    /**
     * 뷰포트의 URL 을 변경한다.
     *
     * @param url - 변경할 URL
     */
    setUrl(url: string, title: string | null = null): void {
        if (this.getUrl() !== url) {
            history.replaceState(null, '', url);
        }

        if (title !== null) {
            document.title = `${title} - ${this.title}`;
        } else {
            document.title = this.title;
        }
    }

    /**
     * 뷰포트 레이아웃을 출력한다.
     */
    async doLayout(): Promise<void> {
        if (this.$getViewport() !== null) {
            this.title = document.title;
            this.properties.renderTo.setAttribute('data-role', 'aui');
            this.properties.renderTo.append(this.$getViewport().getEl());
            this.$getViewport().setAttr('data-viewport', this.id);

            if (this.navigation !== null) {
                const $navigation = Html.create('div', { 'data-region': 'west' });
                $navigation.append(this.navigation.$getComponent());
                this.$getViewport().append($navigation);
                await Renderer.request(() => this.navigation.render());
            }

            let restoreTask: Task = null;
            if (this.taskbar !== null) {
                this.properties.renderTo.setAttribute('data-taskbar', 'true');
                const $taskbar = Html.create('div', { 'data-region': 'north' });
                $taskbar.append(this.taskbar.$getComponent());
                this.$getViewport().append($taskbar);
                await Renderer.request(() => this.taskbar.render());

                restoreTask = await this.taskbar.restoreTasks();
            }

            /**
             * 자동으로 패널을 오픈할 경우
             */
            if (this.hasPanelLoader() == true) {
                const provider = await this.panelLoader(location.pathname);
                if (typeof provider == 'object' && typeof provider.getPanel == 'function') {
                    /**
                     * 작업표시줄로 부터 복원된 작업이 존재할 경우 해당 작업의 URL 과 현재 URL 이 동일한지 확인한다.
                     */
                    if (
                        typeof provider.getUrl == 'function' &&
                        (await restoreTask?.getUrl(true)) == provider.getUrl(true)
                    ) {
                        await restoreTask.setUrl(this.getUrl());
                        await restoreTask.active();
                    } else {
                        const actived =
                            (await this.reactive(provider, this.getUrl())) ||
                            (await this.active(provider, this.getUrl()));

                        if (actived === true) {
                            await this.fireEvent('ready', [this]);
                            return;
                        }
                    }
                }
            }

            if (this.panel === null) {
                if (this.properties.panel === null) {
                    await this.active(this.properties.panel, this.getUrl());
                } else if (this.desktop !== null) {
                    await this.activeDesktop(false);
                }
            }

            await this.fireEvent('ready', [this]);
        }
    }
}
